// Governance → Quality Assurance — the B5 cross-portfolio work cockpit
// (sibling anatomy to the Compliance cockpit): summary strip → work queue
// (QA reviews due per loan cadence + open Exception findings with their
// follow-up task status, read-only) → recently completed reviews. All
// work happens on each loan's Quality Assurance tab (tab key "legal-ops");
// rows link through.

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Card, EmptyState, FilterChips, SearchField, SortSelect, Table, TBody, TD, TH, THead, TR } from "@/design-system/components/ui";
import { useQaWorkQueue } from "@/hooks/compliance/useWorkQueues";
import { extractErrorMessage } from "@/api/client";
import type {
  QaCompletedReviewDto,
  QaWorkQueueRowDto,
  QaWorkQueueType,
} from "@/api/compliance/workQueueTypes";
import {
  dueDelta,
  SeverityDot,
  sortQueueRows,
  SummaryStrip,
} from "@/components/governance/WorkQueuePrimitives";
import { Pagination } from "@/components/layout/Pagination";
import { useDateFormatter } from "@/utils/datetime";

export const QA_TYPE_LABEL: Record<QaWorkQueueType, string> = {
  ReviewDue: "Review due",
  ExceptionFinding: "Exception",
};

/** The loan QA tab's key (display label "Quality Assurance"). */
const QA_TAB = "legal-ops";

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "urgency", label: "Urgency" },
  { value: "due", label: "Due date" },
  { value: "loan", label: "Loan" },
];

export default function QualityAssuranceCockpitPage() {
  const [params, setParams] = useSearchParams();
  const typeFilter = params.get("type") ?? "All";
  const severityFilter = params.get("severity") ?? "All";
  const loanSearch = params.get("loan") ?? "";
  const sort = params.get("sort") ?? "urgency";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const writeParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    next.delete("page");
    setParams(next, { replace: true });
  };
  const setParam = (key: string, value: string, defaultValue: string) =>
    writeParams((p) => {
      if (value === defaultValue) p.delete(key);
      else p.set(key, value);
    });
  const setPage = (next: number) =>
    setParams(
      (p) => {
        const out = new URLSearchParams(p);
        out.set("page", String(next));
        return out;
      },
      { replace: true },
    );

  const queueQuery = useQaWorkQueue();
  const allRows = queueQuery.data?.rows ?? [];

  const filtered = useMemo(() => {
    const search = loanSearch.trim().toLowerCase();
    const rows = allRows
      .filter((r) => (typeFilter === "All" ? true : r.type === typeFilter))
      .filter((r) =>
        severityFilter === "All" ? true : r.severity === severityFilter,
      )
      .filter((r) =>
        search ? (r.loanNumber ?? "").toLowerCase().includes(search) : true,
      );
    return sortQueueRows(rows, sort);
  }, [allRows, typeFilter, severityFilter, loanSearch, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const summary = queueQuery.data?.summary;
  const completed = queueQuery.data?.recentCompletedReviews ?? [];

  return (
    <div className="flex flex-col gap-space-4" data-testid="qa-cockpit">
      <Card>
        <h1 className="font-display font-bold text-h2 m-0">Quality Assurance</h1>
        <p className="text-caption text-gray-mid mt-space-1 m-0">
          Everything QA-related across all loans — reviews due, open exception
          findings, and recently completed reviews. Work happens on each
          loan&apos;s Quality Assurance tab.
        </p>
      </Card>

      {summary && (
        <SummaryStrip
          data-testid="qa-summary-strip"
          tiles={[
            { id: "due", label: "Reviews due", value: summary.reviewsDue, tone: "warning" },
            { id: "overdue", label: "Overdue", value: summary.reviewsOverdue, tone: "error" },
            { id: "exceptions", label: "Open exceptions", value: summary.openExceptions, tone: "error" },
            { id: "awaiting-task", label: "Awaiting task completion", value: summary.awaitingTaskCompletion },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-space-3">
          <FilterChips
            label="Filter by type"
            chips={[
              { id: "All", label: "All types" },
              { id: "ReviewDue", label: "Reviews due" },
              { id: "ExceptionFinding", label: "Exceptions" },
            ]}
            activeId={typeFilter}
            onChange={(id) => setParam("type", id, "All")}
          />
          <FilterChips
            label="Filter by severity"
            chips={[
              { id: "All", label: "All severities" },
              { id: "error", label: "Overdue / exception" },
              { id: "warning", label: "Due soon" },
            ]}
            activeId={severityFilter}
            onChange={(id) => setParam("severity", id, "All")}
          />
          <SearchField
            label="Search by loan number"
            placeholder="Loan number…"
            value={loanSearch}
            onChange={(v) => setParam("loan", v, "")}
            className="w-56"
          />
          <SortSelect
            options={SORT_OPTIONS}
            value={sort}
            onChange={(v) => setParam("sort", v, "urgency")}
          />
        </div>
      </Card>

      {queueQuery.isLoading ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading work queue…
          </p>
        </Card>
      ) : queueQuery.isError ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            {extractErrorMessage(queueQuery.error)}
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        allRows.length === 0 ? (
          <div data-testid="qa-queue-empty">
            <EmptyState
              title="Nothing needs attention"
              description="No QA reviews are due and no exception findings are open across the portfolio."
            />
          </div>
        ) : (
          <div data-testid="qa-queue-filtered-empty">
            <EmptyState
              variant="noResults"
              title="No items match the current filters"
              description="Clear the type, severity, or loan filters to see the full queue."
            />
          </div>
        )
      ) : (
        <Card className="p-0 overflow-x-auto">
          <Table data-testid="qa-queue-table">
            <THead>
              <TR>
                <TH className="w-8"><span className="sr-only">Severity</span></TH>
                <TH>Item</TH>
                <TH>Loan</TH>
                <TH>Due</TH>
                <TH>Follow-up task</TH>
              </TR>
            </THead>
            <TBody>
              {pageRows.map((r) => (
                <QueueRow key={r.id} row={r} />
              ))}
            </TBody>
          </Table>
          {pageCount > 1 && (
            <div className="px-space-3 pb-space-3">
              <Pagination
                page={currentPage}
                pageSize={PAGE_SIZE}
                totalCount={filtered.length}
                totalPages={pageCount}
                onPrev={() => setPage(currentPage - 1)}
                onNext={() => setPage(currentPage + 1)}
              />
            </div>
          )}
        </Card>
      )}

      <section aria-label="Recently completed reviews" className="flex flex-col gap-space-3">
        <Card>
          <h2 className="font-display font-bold text-h3 m-0">
            Recently completed reviews
          </h2>
          <p className="text-caption text-gray-mid mt-space-1 m-0">
            The latest completed QA reviews, with their filed reports.
          </p>
        </Card>
        {queueQuery.isLoading ? (
          <Card>
            <p className="text-small text-gray-mid italic m-0">Loading…</p>
          </Card>
        ) : completed.length === 0 ? (
          <Card data-testid="qa-completed-empty">
            <p className="text-small text-gray-mid italic m-0">
              No QA reviews have been completed yet.
            </p>
          </Card>
        ) : (
          <ul
            aria-label="Completed QA reviews"
            className="divide-y divide-border-light bg-white-pure border border-border-light rounded-lg"
            data-testid="qa-completed-list"
          >
            {completed.map((r) => (
              <CompletedReviewRow key={r.reviewId} review={r} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function QueueRow({ row }: { row: QaWorkQueueRowDto }) {
  const target = `/portfolio-admin/loans/${row.loanId}?tab=${QA_TAB}`;
  return (
    <TR data-testid={`qa-queue-row-${row.id}`}>
      <TD>
        <SeverityDot severity={row.severity} />
      </TD>
      <TD>
        <div className="flex items-center gap-space-2">
          <Badge status="archived">{QA_TYPE_LABEL[row.type]}</Badge>
          <Link
            to={target}
            className="font-semibold text-small text-onyx hover:text-gold-shadow hover:underline"
            data-testid={`qa-queue-link-${row.id}`}
          >
            {row.title}
          </Link>
        </div>
        {row.detail && (
          <p className="text-caption text-gray-mid mt-space-1 m-0 truncate max-w-xl">
            {row.detail}
          </p>
        )}
      </TD>
      <TD>
        <Link to={target} className="text-small text-gold-shadow hover:underline">
          {row.loanNumber ?? "View loan"}
        </Link>
      </TD>
      {/* The viewer's zone, not `dueDelta`'s Phoenix fallback — `dueDate` is
          a wall-calendar `DateOnly`. */}
      <TD className="whitespace-nowrap text-small">{dueDelta(row.dueDate)}</TD>
      <TD data-testid={`qa-queue-task-${row.id}`}>
        {row.type !== "ExceptionFinding" ? (
          <span className="text-caption text-gray-mid">—</span>
        ) : row.followUpTaskId ? (
          <Badge status={row.followUpTaskStatus === "InProgress" ? "info" : "draft"}>
            {row.followUpTaskStatus ?? "Open"}
          </Badge>
        ) : (
          <span className="text-caption text-gray-mid italic">No task yet</span>
        )}
      </TD>
    </TR>
  );
}

function CompletedReviewRow({ review }: { review: QaCompletedReviewDto }) {
  const { date } = useDateFormatter();
  const target = `/portfolio-admin/loans/${review.loanId}?tab=${QA_TAB}`;
  return (
    <li
      className="px-space-3 py-space-3 flex items-center gap-space-3"
      data-testid={`qa-completed-row-${review.reviewId}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-space-2">
          <Link
            to={target}
            className="font-display font-semibold text-small text-onyx hover:text-gold-shadow hover:underline"
          >
            {review.loanNumber ?? "Loan"}
          </Link>
          <span className="text-caption text-gray-mid">
            reviewed {date(review.reviewDate)}
            {review.reviewedByUserName && <> by {review.reviewedByUserName}</>}
          </span>
        </div>
        <div className="mt-space-1 text-caption text-gray-mid">
          {review.findingCount} finding{review.findingCount === 1 ? "" : "s"}
          {review.reportLoanReportId && (
            <>
              {" "}
              ·{" "}
              <Link
                to={target}
                className="text-gold-shadow hover:underline"
                data-testid={`qa-completed-report-${review.reviewId}`}
              >
                {review.reportStatus ? `Report (${review.reportStatus})` : "Report"}
              </Link>
            </>
          )}
        </div>
      </div>
    </li>
  );
}
